import { type FC } from 'react';
import classnames from 'classnames';
import dibsCss from 'dibs-css';
import { Star } from 'dibs-elements/exports/Star';
import { FormattedMessage, FormattedNumber } from 'dibs-react-intl';

type Props = {
    rating: number;
    totalReviews?: number | null;
    variant?: 'inline' | 'stacked';
    className?: string;
    ratingTextClassName?: string;
    showTotalReviews?: boolean;
};

export const SellerReviewsSummary: FC<Props> = ({
    rating,
    totalReviews,
    variant = 'inline',
    className,
    ratingTextClassName,
    showTotalReviews = true,
}) => {
    const reviewsFromVerifiedBuyersMsg = (
        <FormattedMessage
            id="SellerReviews.reviewsFromVerifiedBuyers"
            defaultMessage="{totalReviews, number} reviews from verified buyers"
            values={{ totalReviews }}
        />
    );

    const minimumFractionDigits = rating % 1 === 0 ? 0 : 1;
    const ratingOutOfFiveText = (
        <span className={ratingTextClassName || dibsCss.sassyFontBodyLargeHeavy}>
            <FormattedMessage
                id="SellerReviews.ratingOutOfFive"
                defaultMessage="{rating} out of 5"
                values={{
                    rating: (
                        <FormattedNumber
                            style="decimal"
                            value={rating}
                            minimumFractionDigits={minimumFractionDigits}
                        />
                    ),
                }}
            />
        </span>
    );

    if (variant === 'stacked') {
        return (
            <div className={className}>
                <div className={classnames(dibsCss.inlineFlex, dibsCss.itemsCenter)}>
                    <span
                        className={classnames(
                            dibsCss.inlineFlex,
                            dibsCss.itemsCenter,
                            dibsCss.mrXxsmall
                        )}
                    >
                        <Star percentFilled={100} size="smaller" />
                    </span>
                    {ratingOutOfFiveText}
                </div>
                {showTotalReviews && typeof totalReviews === 'number' && (
                    <div
                        className={classnames(
                            dibsCss.sassyFontBodyMedium,
                            dibsCss.mtXxsmall,
                            dibsCss.mbSmall
                        )}
                    >
                        {reviewsFromVerifiedBuyersMsg}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className={classnames(dibsCss.inlineFlex, dibsCss.itemsCenter, className)}>
            <span
                className={classnames(dibsCss.inlineFlex, dibsCss.itemsCenter, dibsCss.mrXxsmall)}
            >
                <Star percentFilled={100} size="smaller" />
            </span>
            {ratingOutOfFiveText}
            {showTotalReviews && typeof totalReviews === 'number' && (
                <span className={classnames(dibsCss.mlXsmall, dibsCss.sassyFontBodyMedium)}>
                    ({reviewsFromVerifiedBuyersMsg})
                </span>
            )}
        </div>
    );
};
